import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react'
import {
  X,
  AlertTriangle,
  Plus,
  Trash2,
  CheckCircle2,
  Loader2,
  Building2,
  Calendar,
  Percent,
  FileSpreadsheet,
  ClipboardPaste,
  ChevronDown,
} from 'lucide-react'
import {
  type ExtractedQuoteLineDto,
  type ExtractQuoteResultData,
  type FileMetadataDto,
  confirmAiQuoteApi,
} from './api'
import { listSuppliersApi } from '@/services/suppliers/apiClient'
import type { Supplier } from '@/services/suppliers/types'
import { materialService } from '@/services/materials'
import type { Material } from '@/services/materials/types'
import { DateInput } from '@/components/forms/DateInput'
import { SheetAmountInput } from '@/components/forms/SheetAmountInput'
import { parseSheetAmount } from '@/lib/sheetAmount'

interface QuotePreviewModalProps {
  isOpen: boolean
  data: ExtractQuoteResultData
  fileMetadata?: FileMetadataDto | null
  onClose: () => void
  onSuccess: (quoteResult: { quote_id?: string; title: string; count: number }) => void
}

interface AutoResizeTextareaProps {
  value: string
  onChange: (value: string) => void
  placeholder?: string
  className?: string
  title?: string
  ariaLabel?: string
  minRows?: number
}

const AutoResizeTextarea: React.FC<AutoResizeTextareaProps> = ({
  value,
  onChange,
  placeholder,
  className = '',
  title,
  ariaLabel,
  minRows = 1,
}) => {
  const ref = useRef<HTMLTextAreaElement>(null)

  const adjustHeight = useCallback(() => {
    const el = ref.current
    if (!el) return
    el.style.height = 'auto'
    const newHeight = Math.max(28, el.scrollHeight)
    el.style.height = `${newHeight}px`
  }, [])

  useEffect(() => {
    adjustHeight()
  }, [value, adjustHeight])

  useEffect(() => {
    window.addEventListener('resize', adjustHeight)
    return () => window.removeEventListener('resize', adjustHeight)
  }, [adjustHeight])

  return (
    <textarea
      ref={ref}
      rows={minRows}
      value={value}
      title={title ?? value}
      aria-label={ariaLabel}
      onChange={(e) => {
        onChange(e.target.value)
        adjustHeight()
      }}
      placeholder={placeholder}
      className={`w-full resize-none overflow-hidden rounded px-2 py-1 text-xs border border-transparent hover:border-slate-300 focus:border-[var(--color-primary)] focus:bg-white focus:outline-none transition-colors whitespace-pre-wrap break-words leading-snug ${className}`}
    />
  )
}

const DEFAULT_COL_WIDTHS: Record<string, number> = {
  stt: 42,
  source: 105,
  name: 215,
  spec: 145,
  uom: 55,
  qty: 75,
  price: 105,
  mapping: 230,
  note: 205,
  action: 43,
}

const COLUMNS = [
  { key: 'stt', label: 'STT', align: 'center', resizable: false },
  { key: 'source', label: 'Nguồn', align: 'left', resizable: true },
  { key: 'name', label: 'Tên hàng *', align: 'left', resizable: true },
  { key: 'spec', label: 'Quy cách', align: 'left', resizable: true },
  { key: 'uom', label: 'ĐVT', align: 'center', resizable: true },
  { key: 'qty', label: 'Số lượng', align: 'right', resizable: true },
  { key: 'price', label: 'Đơn giá *', align: 'right', resizable: true },
  { key: 'mapping', label: 'Ánh xạ vật tư (ERP)', align: 'left', resizable: true },
  { key: 'note', label: 'Ghi chú', align: 'left', resizable: true },
  { key: 'action', label: 'Xóa', align: 'center', resizable: false },
]

export const QuotePreviewModal: React.FC<QuotePreviewModalProps> = ({
  isOpen,
  data,
  fileMetadata,
  onClose,
  onSuccess,
}) => {
  const [suppliers, setSuppliers] = useState<Supplier[]>([])
  const [materials, setMaterials] = useState<Material[]>([])
  const [selectedSupplierId, setSelectedSupplierId] = useState<string>('')
  const [quoteDate, setQuoteDate] = useState<string>(
    data.quote_date || new Date().toISOString().slice(0, 10),
  )
  const [vatRate, setVatRate] = useState<number>(data.vat_rate ?? 8)
  const [title, setTitle] = useState<string>(() => {
    const suppName = data.matched_supplier?.name || data.supplier_name || 'NCC'
    const dateVal = data.quote_date || new Date().toISOString().slice(0, 10)
    return `Báo giá AI ${suppName} ${dateVal}`
  })
  const [lines, setLines] = useState<ExtractedQuoteLineDto[]>(data.lines || [])
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [submitError, setSubmitError] = useState<string | null>(null)
  const [pasteDialogOpen, setPasteDialogOpen] = useState(false)
  const [pastedContent, setPastedContent] = useState('')

  // Column width state for spreadsheet-like resizable columns
  const [colWidths, setColWidths] = useState<Record<string, number>>(DEFAULT_COL_WIDTHS)
  const [resizingCol, setResizingCol] = useState<{
    key: string
    startX: number
    startWidth: number
  } | null>(null)

  // Listen for column resizing mouse drag
  useEffect(() => {
    if (!resizingCol) return

    const handleMouseMove = (e: MouseEvent) => {
      const diff = e.clientX - resizingCol.startX
      const newWidth = Math.max(40, resizingCol.startWidth + diff)
      setColWidths((prev) => ({ ...prev, [resizingCol.key]: newWidth }))
    }

    const handleMouseUp = () => {
      setResizingCol(null)
    }

    window.addEventListener('mousemove', handleMouseMove)
    window.addEventListener('mouseup', handleMouseUp)
    return () => {
      window.removeEventListener('mousemove', handleMouseMove)
      window.removeEventListener('mouseup', handleMouseUp)
    }
  }, [resizingCol])

  const handleDoubleClickResizer = (colKey: string) => {
    let estimated = DEFAULT_COL_WIDTHS[colKey] || 100
    if (colKey === 'source') {
      const maxLen = Math.max(8, ...lines.map((l) => (l.source_ref || '').length))
      estimated = Math.max(105, Math.min(350, maxLen * 7.5 + 24))
    } else if (colKey === 'name') {
      const maxLen = Math.max(10, ...lines.map((l) => (l.supplier_item_name || '').length))
      estimated = Math.max(180, Math.min(500, maxLen * 8 + 24))
    } else if (colKey === 'spec') {
      const maxLen = Math.max(8, ...lines.map((l) => (l.spec || '').length))
      estimated = Math.max(120, Math.min(350, maxLen * 8 + 24))
    } else if (colKey === 'note') {
      const maxLen = Math.max(8, ...lines.map((l) => (l.note || '').length))
      estimated = Math.max(160, Math.min(450, maxLen * 8 + 24))
    } else if (colKey === 'mapping') {
      estimated = 230
    }
    setColWidths((prev) => ({ ...prev, [colKey]: Math.round(estimated) }))
  }

  // Load suppliers and materials
  useEffect(() => {
    let cancelled = false
    Promise.all([listSuppliersApi().catch(() => []), materialService.list().catch(() => [])]).then(
      ([suppList, matList]) => {
        if (cancelled) return
        setSuppliers(suppList)
        setMaterials(matList)

        // Pre-select supplier
        if (data.matched_supplier?.id) {
          setSelectedSupplierId(data.matched_supplier.id)
        } else if (data.supplier_name) {
          const match = suppList.find(
            (s) =>
              s.name.toLowerCase().includes(data.supplier_name!.toLowerCase()) ||
              data.supplier_name!.toLowerCase().includes(s.name.toLowerCase()),
          )
          if (match) setSelectedSupplierId(match.id)
        }
      },
    )
    return () => {
      cancelled = true
    }
  }, [data])

  // Update title when supplier or date changes
  const computedTitle = useMemo(() => {
    const supp = suppliers.find((s) => s.id === selectedSupplierId)
    const suppName = supp?.name || data.supplier_name || 'NCC'
    return `Báo giá AI ${suppName} ${quoteDate}`
  }, [selectedSupplierId, quoteDate, suppliers, data.supplier_name])

  useEffect(() => {
    setTitle(computedTitle)
  }, [computedTitle])

  // Validation
  const validationErrors = useMemo(() => {
    const errors: string[] = []
    if (!selectedSupplierId) {
      errors.push('Vui lòng chọn Nhà cung cấp.')
    }
    if (lines.length === 0) {
      errors.push('Bảng báo giá không có dòng hàng hóa nào.')
    }
    for (let i = 0; i < lines.length; i++) {
      const l = lines[i]
      if (!l.supplier_item_name?.trim()) {
        errors.push(`Dòng STT ${i + 1}: Thiếu tên hàng hóa.`)
      }
      if (l.unit_price === null || l.unit_price === undefined || l.unit_price < 0) {
        errors.push(`Dòng STT ${i + 1}: Đơn giá không hợp lệ (phải >= 0).`)
      }
    }
    return errors
  }, [selectedSupplierId, lines])

  const warningCount = useMemo(() => {
    return lines.filter((l) => l.has_warning).length
  }, [lines])

  const totalTableWidth = useMemo(
    () => Object.values(colWidths).reduce((a, b) => a + b, 0),
    [colWidths],
  )

  if (!isOpen) return null

  const handleUpdateLine = (index: number, patch: Partial<ExtractedQuoteLineDto>) => {
    setLines((prev) => {
      const copy = [...prev]
      copy[index] = { ...copy[index], ...patch }
      return copy
    })
  }

  const handleDeleteLine = (index: number) => {
    setLines((prev) => prev.filter((_, i) => i !== index))
  }

  const handleAddLine = () => {
    const newLine: ExtractedQuoteLineDto = {
      id: `line-user-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`,
      supplier_item_name: '',
      spec: null,
      uom: 'Pcs',
      quantity: null,
      unit_price: 0,
      note: null,
      source_ref: 'Thêm thủ công',
      item_id: null,
      has_warning: false,
    }
    setLines((prev) => [...prev, newLine])
  }

  const handleApplyPastedContent = () => {
    const text = pastedContent.trim()
    if (!text) return

    try {
      // Try JSON format
      if (text.startsWith('{') || text.startsWith('[')) {
        const parsed = JSON.parse(text)
        const incomingLines = Array.isArray(parsed) ? parsed : parsed.lines
        if (Array.isArray(incomingLines)) {
          const mapped: ExtractedQuoteLineDto[] = incomingLines.map((l: any, idx: number) => ({
            id: `pasted-${Date.now()}-${idx}`,
            supplier_item_name: String(l.supplier_item_name || l.name || ''),
            spec: l.spec ? String(l.spec) : null,
            uom: l.uom ? String(l.uom) : 'Pcs',
            quantity: typeof l.quantity === 'number' ? l.quantity : null,
            unit_price:
              typeof l.unit_price === 'number'
                ? l.unit_price
                : Number(l.price) || parseSheetAmount(String(l.price ?? '')) || 0,
            note: l.note ? String(l.note) : null,
            source_ref: 'Dán ngoài',
            item_id: null,
          }))
          setLines((prev) => [...prev, ...mapped])
          setPasteDialogOpen(false)
          setPastedContent('')
          return
        }
      }

      // Tab/CSV format
      const rows = text.split(/\r?\n/).filter(Boolean)
      const mapped: ExtractedQuoteLineDto[] = []
      for (let r = 0; r < rows.length; r++) {
        const cols = rows[r].split(/\t|,|;|\|/).map((c) => c.trim())
        if (cols.length === 0 || !cols[0]) continue
        // skip if looks like header
        if (
          r === 0 &&
          (cols[0].toLowerCase().includes('tên') || cols[0].toLowerCase().includes('stt'))
        )
          continue

        const name = cols[0]
        const priceParsed = cols[1] ? parseSheetAmount(cols[1]) : 0
        mapped.push({
          id: `pasted-row-${Date.now()}-${r}`,
          supplier_item_name: name,
          spec: null,
          uom: 'Pcs',
          quantity: null,
          unit_price: priceParsed ?? 0,
          note: null,
          source_ref: 'Dán ngoài',
          item_id: null,
        })
      }
      if (mapped.length > 0) {
        setLines((prev) => [...prev, ...mapped])
      }
      setPasteDialogOpen(false)
      setPastedContent('')
    } catch (err) {
      alert(`Không phân tích được nội dung dán: ${err instanceof Error ? err.message : String(err)}`)
    }
  }

  const handleConfirm = async () => {
    if (validationErrors.length > 0 || isSubmitting) return

    setIsSubmitting(true)
    setSubmitError(null)

    try {
      const payload = {
        supplier_id: selectedSupplierId,
        title,
        quote_date: quoteDate,
        vat_rate: Number(vatRate),
        currency: data.currency || 'VND',
        file: fileMetadata
          ? {
              original_file_name: fileMetadata.original_file_name,
              content_type: fileMetadata.content_type,
              bytes_base64: fileMetadata.bytes_base64 || '',
            }
          : null,
        lines: lines.map((l) => ({
          supplier_item_name: l.supplier_item_name.trim(),
          spec: l.spec || null,
          uom: l.uom?.trim() || 'Pcs',
          unit_price: Number(l.unit_price) || 0,
          item_id: l.item_id || null, // defaults to null -> NEEDS_MAPPING in existing price rules
          note: l.note || null,
          source_ref: l.source_ref || null,
          quantity: l.quantity || null,
        })),
      }

      const res = await confirmAiQuoteApi(payload)
      const quoteId = res?.quote?.id || res?.quote_id
      onSuccess({
        quote_id: quoteId,
        title,
        count: lines.length,
      })
    } catch (err) {
      setSubmitError(err instanceof Error ? err.message : 'Có lỗi xảy ra khi xác nhận báo giá.')
    } finally {
      setIsSubmitting(false)
    }
  }

  return (
    <div
      role="dialog"
      aria-label="Bảng xem trước báo giá NCC từ AI"
      aria-modal="true"
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-2 sm:p-4 print:hidden backdrop-blur-xs"
    >
      <div className="flex flex-col h-[85vh] max-h-[85vh] w-[96vw] max-w-[96vw] rounded-xl bg-white shadow-2xl border border-slate-200 overflow-hidden">
        {/* Header - Fixed top */}
        <div className="shrink-0 flex items-center justify-between border-b border-slate-200 bg-slate-50 px-6 py-4">
          <div className="flex items-center gap-3">
            <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-[var(--color-primary)] text-white shadow-xs">
              <FileSpreadsheet className="h-5 w-5" />
            </div>
            <div>
              <div className="flex items-center gap-2">
                <h2 className="text-base font-semibold text-slate-800">
                  Xem trước & Xác nhận Báo giá Nhà cung cấp
                </h2>
                <span className="rounded bg-sky-100 px-2 py-0.5 text-xs font-medium text-sky-800">
                  AI Chuẩn hóa
                </span>
                {fileMetadata && (
                  <span className="text-xs text-slate-500 font-mono">
                    ({fileMetadata.original_file_name})
                  </span>
                )}
              </div>
              <p className="text-xs text-slate-500 mt-0.5">
                Kiểm tra, sửa thông tin các dòng giá trước khi ghi vào hệ thống. Dòng chưa ánh xạ sẽ vào trạng thái NEEDS_MAPPING.
              </p>
            </div>
          </div>

          <button
            type="button"
            onClick={onClose}
            className="rounded-lg p-2 text-slate-400 hover:bg-slate-200 hover:text-slate-700 transition-colors"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* Metadata Controls Toolbar - Fixed */}
        <div className="shrink-0 border-b border-slate-200 bg-white p-4 grid grid-cols-1 md:grid-cols-4 gap-4 items-end">
          {/* Supplier Picker */}
          <div>
            <label className="block text-xs font-semibold text-slate-700 mb-1 flex items-center gap-1.5">
              <Building2 className="h-3.5 w-3.5 text-slate-500" />
              <span>Nhà cung cấp *</span>
            </label>
            <select
              value={selectedSupplierId}
              onChange={(e) => setSelectedSupplierId(e.target.value)}
              className="w-full rounded-md border border-slate-300 bg-white px-3 py-1.5 text-xs text-slate-800 focus:border-[var(--color-primary)] focus:outline-none focus:ring-1 focus:ring-[var(--color-primary)]"
            >
              <option value="">-- Chọn Nhà cung cấp --</option>
              {suppliers.map((s) => (
                <option key={s.id} value={s.id}>
                  {s.code ? `[${s.code}] ` : ''}{s.name}
                </option>
              ))}
            </select>
          </div>

          {/* Quote Date with full year (dd/mm/yyyy) */}
          <div>
            <label className="block text-xs font-semibold text-slate-700 mb-1 flex items-center gap-1.5">
              <Calendar className="h-3.5 w-3.5 text-slate-500" />
              <span>Ngày báo giá</span>
            </label>
            <DateInput
              value={quoteDate}
              onChange={setQuoteDate}
              formatFull={true}
              className="w-full text-xs"
              placeholder="dd/mm/yyyy"
            />
          </div>

          {/* VAT Rate */}
          <div>
            <label className="block text-xs font-semibold text-slate-700 mb-1 flex items-center gap-1.5">
              <Percent className="h-3.5 w-3.5 text-slate-500" />
              <span>Thuế suất VAT (%)</span>
            </label>
            <input
              type="number"
              value={vatRate}
              onChange={(e) => setVatRate(Number(e.target.value) || 0)}
              className="w-full rounded-md border border-slate-300 bg-white px-3 py-1.5 text-xs text-slate-800 focus:border-[var(--color-primary)] focus:outline-none focus:ring-1 focus:ring-[var(--color-primary)] [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
            />
          </div>

          {/* Quick Actions */}
          <div className="flex items-center gap-2 justify-end">
            <button
              type="button"
              onClick={() => setPasteDialogOpen(true)}
              className="flex items-center gap-1.5 rounded-md border border-slate-300 bg-white px-3 py-1.5 text-xs font-medium text-slate-700 hover:bg-slate-50 shadow-2xs"
            >
              <ClipboardPaste className="h-3.5 w-3.5 text-slate-500" />
              <span>Dán ngoài (JSON/Bảng)</span>
            </button>
            <button
              type="button"
              onClick={handleAddLine}
              className="flex items-center gap-1.5 rounded-md border border-[var(--color-primary)] bg-white px-3 py-1.5 text-xs font-medium text-[var(--color-primary)] hover:bg-[var(--color-primary-soft)]/20 shadow-2xs"
            >
              <Plus className="h-3.5 w-3.5" />
              <span>Thêm dòng</span>
            </button>
          </div>
        </div>

        {/* Warning notification bar if any - Fixed */}
        {warningCount > 0 && (
          <div className="shrink-0 bg-amber-50 border-b border-amber-200 px-6 py-2 flex items-center gap-2 text-xs text-amber-800 flex-wrap">
            <AlertTriangle className="h-4 w-4 text-amber-600 shrink-0" />
            <span>
              Có <strong>{warningCount}</strong> dòng có số đơn giá hoặc số lượng cần kiểm tra lại (được tô vàng vì không khớp số trong văn bản gốc).
            </span>
          </div>
        )}

        {/* Main Editable Preview Table (Excel-like Workspace with Resizable Columns) */}
        <div className="flex-1 overflow-auto bg-slate-100 p-3 sm:p-4">
          <div className="rounded-lg border border-slate-200 bg-white shadow-xs overflow-x-auto">
            <table
              className="text-left text-xs text-slate-700 border-collapse table-fixed w-full"
              style={{ minWidth: totalTableWidth }}
            >
              <colgroup>
                {COLUMNS.map((col) => (
                  <col key={col.key} style={{ width: colWidths[col.key] }} />
                ))}
              </colgroup>
              <thead className="bg-slate-100 text-[11px] font-semibold uppercase tracking-wider text-slate-600 border-b border-slate-200 sticky top-0 z-10 shadow-2xs">
                <tr>
                  {COLUMNS.map((col) => {
                    const isStt = col.key === 'stt'
                    const alignClass =
                      col.align === 'right'
                        ? 'text-right'
                        : col.align === 'center'
                        ? 'text-center'
                        : 'text-left'
                    return (
                      <th
                        key={col.key}
                        style={
                          isStt
                            ? {
                                width: colWidths[col.key],
                                position: 'sticky',
                                left: 0,
                                zIndex: 20,
                                backgroundColor: '#f1f5f9',
                              }
                            : {
                                width: colWidths[col.key],
                              }
                        }
                        className={`relative px-2.5 py-2 border-r border-slate-200 select-none ${alignClass} ${
                          isStt ? 'shadow-r' : ''
                        }`}
                      >
                        <span className="block leading-tight">{col.label}</span>
                        {col.resizable && (
                          <span
                            role="separator"
                            aria-label={`Đổi độ rộng cột ${col.label}`}
                            title="Kéo để đổi độ rộng · Nhấp đúp để tự vừa"
                            className="absolute right-0 top-0 h-full w-2 cursor-col-resize hover:bg-sky-400 active:bg-sky-600 transition-colors z-30"
                            onMouseDown={(e) => {
                              e.preventDefault()
                              e.stopPropagation()
                              setResizingCol({
                                key: col.key,
                                startX: e.clientX,
                                startWidth: colWidths[col.key],
                              })
                            }}
                            onDoubleClick={(e) => {
                              e.preventDefault()
                              e.stopPropagation()
                              handleDoubleClickResizer(col.key)
                            }}
                          />
                        )}
                      </th>
                    )
                  })}
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-200">
                {lines.length === 0 ? (
                  <tr>
                    <td colSpan={10} className="px-4 py-8 text-center text-slate-400">
                      Chưa có dòng dữ liệu nào. Bấm &quot;Thêm dòng&quot; hoặc &quot;Dán ngoài&quot; để bổ sung.
                    </td>
                  </tr>
                ) : (
                  lines.map((line, idx) => {
                    const isYellow = line.has_warning
                    const rowBg = isYellow ? 'bg-amber-50/60' : 'bg-white hover:bg-slate-50/80'
                    const sttBg = isYellow ? '#fef3c7' : '#ffffff'
                    return (
                      <tr key={line.id} className={`${rowBg} transition-colors`}>
                        {/* STT - Sticky left with higher z-index than body cells */}
                        <td
                          style={{
                            position: 'sticky',
                            left: 0,
                            zIndex: 10,
                            backgroundColor: sttBg,
                          }}
                          className="px-2 py-1.5 text-center font-mono text-slate-500 border-r border-slate-100 shadow-r"
                        >
                          {idx + 1}
                        </td>

                        {/* Nguồn - Multi-line wrapping */}
                        <td className="px-2 py-1.5 border-r border-slate-100">
                          <div
                            className="rounded bg-slate-100 px-2 py-1 text-[11px] text-slate-700 font-mono whitespace-normal break-words leading-tight"
                            title={line.source_ref || 'Không có nguồn'}
                          >
                            {line.source_ref || '-'}
                          </div>
                        </td>

                        {/* Tên hàng - AutoResizeTextarea multi-line */}
                        <td className="px-2 py-1 border-r border-slate-100">
                          <AutoResizeTextarea
                            value={line.supplier_item_name}
                            onChange={(val) =>
                              handleUpdateLine(idx, { supplier_item_name: val })
                            }
                            placeholder="Nhập tên hàng hóa..."
                            ariaLabel={`Tên hàng dòng ${idx + 1}`}
                          />
                        </td>

                        {/* Quy cách - AutoResizeTextarea multi-line */}
                        <td className="px-2 py-1 border-r border-slate-100">
                          <AutoResizeTextarea
                            value={line.spec || ''}
                            onChange={(val) =>
                              handleUpdateLine(idx, { spec: val || null })
                            }
                            placeholder="Quy cách..."
                            ariaLabel={`Quy cách dòng ${idx + 1}`}
                          />
                        </td>

                        {/* ĐVT */}
                        <td className="px-2 py-1 border-r border-slate-100 text-center">
                          <input
                            type="text"
                            value={line.uom || ''}
                            title={line.uom || ''}
                            aria-label={`ĐVT dòng ${idx + 1}`}
                            onChange={(e) =>
                              handleUpdateLine(idx, { uom: e.target.value || 'Pcs' })
                            }
                            placeholder="ĐVT..."
                            className="w-full rounded px-1.5 py-1 text-xs text-center border border-transparent hover:border-slate-300 focus:border-[var(--color-primary)] focus:bg-white focus:outline-none"
                          />
                        </td>

                        {/* Số lượng */}
                        <td className="px-2 py-1 border-r border-slate-100 text-right">
                          <input
                            type="text"
                            inputMode="numeric"
                            value={line.quantity != null ? String(line.quantity) : ''}
                            aria-label={`Số lượng dòng ${idx + 1}`}
                            onChange={(e) => {
                              const val = e.target.value.trim()
                              handleUpdateLine(idx, {
                                quantity: val ? Number(val.replace(/,/g, '')) : null,
                              })
                            }}
                            placeholder="-"
                            className="w-full text-right rounded px-1.5 py-1 text-xs border border-transparent hover:border-slate-300 focus:border-[var(--color-primary)] focus:bg-white focus:outline-none"
                          />
                        </td>

                        {/* Đơn giá - Vietnamese number format (thousands separators), no spinners */}
                        <td className="px-2 py-1 border-r border-slate-100 text-right">
                          <div className="relative">
                            <SheetAmountInput
                              value={line.unit_price}
                              placeholder="0"
                              aria-label={`Đơn giá dòng ${idx + 1}`}
                              className={`w-full text-right font-medium rounded px-2 py-1 text-xs border ${
                                isYellow
                                  ? 'border-amber-400 bg-amber-100/70 text-amber-900 focus:border-amber-500'
                                  : 'border-transparent hover:border-slate-300 focus:border-[var(--color-primary)]'
                              } focus:bg-white focus:outline-none`}
                              onTextChange={(text) => {
                                const parsed = parseSheetAmount(text)
                                handleUpdateLine(idx, {
                                  unit_price: parsed !== null ? parsed : 0,
                                })
                              }}
                            />
                            {isYellow && (
                              <span
                                title={line.warnings?.join('; ') || 'Cần kiểm tra lại con số'}
                                className="absolute left-1 top-2.5 h-2 w-2 rounded-full bg-amber-500 ring-2 ring-amber-200 cursor-help"
                              />
                            )}
                          </div>
                        </td>

                        {/* Ánh xạ vật tư - Compact with multi-line wrapping label */}
                        <td className="px-2 py-1 border-r border-slate-100">
                          <div
                            className={`group relative w-full min-h-[28px] rounded border transition-colors ${
                              line.item_id
                                ? 'border-emerald-300 bg-emerald-50/50 text-emerald-900 font-medium'
                                : 'border-slate-200 bg-white text-slate-600 hover:border-slate-300'
                            } focus-within:border-[var(--color-primary)] focus-within:ring-1 focus-within:ring-[var(--color-primary)]`}
                          >
                            <div className="px-2 py-1 pr-6 text-xs whitespace-normal break-words leading-tight select-none">
                              {(() => {
                                if (!line.item_id) return '-- Chưa ánh xạ (NEEDS_MAPPING) --'
                                const mat = materials.find((m) => m.id === line.item_id)
                                if (!mat) return line.item_id
                                return `${mat.code ? `[${mat.code}] ` : ''}${mat.name}`
                              })()}
                            </div>
                            <div className="absolute right-1.5 top-2 pointer-events-none text-slate-400">
                              <ChevronDown className="h-3.5 w-3.5" />
                            </div>
                            <select
                              value={line.item_id || ''}
                              title={
                                line.item_id
                                  ? materials.find((m) => m.id === line.item_id)?.name || line.item_id
                                  : '-- Chưa ánh xạ (NEEDS_MAPPING) --'
                              }
                              aria-label={`Ánh xạ vật tư dòng ${idx + 1}`}
                              onChange={(e) =>
                                handleUpdateLine(idx, { item_id: e.target.value || null })
                              }
                              className="absolute inset-0 w-full h-full opacity-0 cursor-pointer text-xs"
                            >
                              <option value="">-- Chưa ánh xạ (NEEDS_MAPPING) --</option>
                              {line.suggested_materials && line.suggested_materials.length > 0 && (
                                <optgroup label="Gợi ý từ AI:">
                                  {line.suggested_materials.map((m) => (
                                    <option key={`sug-${m.id}`} value={m.id}>
                                      ★ [{m.score}%] {m.code ? `${m.code} - ` : ''}{m.name} ({m.reason})
                                    </option>
                                  ))}
                                </optgroup>
                              )}
                              <optgroup label="Tất cả vật tư:">
                                {materials.map((m) => (
                                  <option key={`all-${m.id}`} value={m.id}>
                                    {m.code ? `[${m.code}] ` : ''}{m.name}
                                  </option>
                                ))}
                              </optgroup>
                            </select>
                          </div>
                        </td>

                        {/* Ghi chú - AutoResizeTextarea multi-line */}
                        <td className="px-2 py-1 border-r border-slate-100">
                          <AutoResizeTextarea
                            value={line.note || ''}
                            onChange={(val) =>
                              handleUpdateLine(idx, { note: val || null })
                            }
                            placeholder="Ghi chú..."
                            ariaLabel={`Ghi chú dòng ${idx + 1}`}
                          />
                        </td>

                        {/* Xóa dòng */}
                        <td className="px-2 py-1 text-center">
                          <button
                            type="button"
                            onClick={() => handleDeleteLine(idx)}
                            className="p-1 rounded text-slate-400 hover:text-rose-600 hover:bg-rose-50 transition-colors"
                            title="Xóa dòng"
                            aria-label={`Xóa dòng ${idx + 1}`}
                          >
                            <Trash2 className="h-3.5 w-3.5" />
                          </button>
                        </td>
                      </tr>
                    )
                  })
                )}
              </tbody>
            </table>
          </div>
        </div>

        {/* Footer - Fixed bottom, buttons always visible */}
        <div className="shrink-0 border-t border-slate-200 bg-slate-50 px-6 py-3.5 flex flex-col sm:flex-row items-center justify-between gap-3">
          <div className="text-xs text-slate-600 flex-1 min-w-0 pr-4 whitespace-normal break-words">
            {submitError && (
              <span className="text-rose-600 font-medium block">
                {submitError}
              </span>
            )}
            {!submitError && validationErrors.length > 0 && (
              <span className="text-amber-700 font-medium block">
                Chưa thể xác nhận: {validationErrors[0]}
              </span>
            )}
            {!submitError && validationErrors.length === 0 && (
              <span className="text-slate-500">
                Tổng cộng <strong>{lines.length}</strong> dòng hàng hóa sẵn sàng ghi vào hệ thống.
              </span>
            )}
          </div>

          <div className="flex items-center gap-3 shrink-0">
            <button
              type="button"
              onClick={onClose}
              disabled={isSubmitting}
              className="rounded-lg border border-slate-300 bg-white px-4 py-2 text-xs font-medium text-slate-700 hover:bg-slate-100 transition-colors shadow-2xs"
            >
              Bỏ qua
            </button>
            <button
              type="button"
              onClick={handleConfirm}
              disabled={validationErrors.length > 0 || isSubmitting}
              className="flex items-center gap-2 rounded-lg bg-[var(--color-primary)] px-5 py-2 text-xs font-medium text-white shadow-xs hover:bg-[var(--color-primary-hover)] disabled:opacity-40 disabled:cursor-not-allowed transition-all"
            >
              {isSubmitting ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin" />
                  <span>Đang ghi báo giá...</span>
                </>
              ) : (
                <>
                  <CheckCircle2 className="h-4 w-4" />
                  <span>Xác nhận nhập báo giá</span>
                </>
              )}
            </button>
          </div>
        </div>
      </div>

      {/* Paste Dialog */}
      {pasteDialogOpen && (
        <div className="fixed inset-0 z-60 flex items-center justify-center bg-black/50 p-4">
          <div className="w-full max-w-lg rounded-xl bg-white shadow-xl border border-slate-200 p-5 space-y-4">
            <div className="flex items-center justify-between">
              <h3 className="text-sm font-semibold text-slate-800">
                Dán kết quả từ AI / Công cụ khác
              </h3>
              <button
                type="button"
                onClick={() => setPasteDialogOpen(false)}
                className="text-slate-400 hover:text-slate-600"
              >
                <X className="h-4 w-4" />
              </button>
            </div>
            <p className="text-xs text-slate-500">
              Dán văn bản bảng (phân cách bằng Tab, phẩy, gạch đứng) hoặc dữ liệu JSON theo schema. Hệ thống sẽ tự nạp vào bảng xem trước.
            </p>
            <textarea
              rows={8}
              value={pastedContent}
              onChange={(e) => setPastedContent(e.target.value)}
              placeholder="Dán JSON hoặc bảng tại đây..."
              className="w-full rounded-lg border border-slate-300 p-3 text-xs font-mono focus:border-[var(--color-primary)] focus:outline-none"
            />
            <div className="flex justify-end gap-2">
              <button
                type="button"
                onClick={() => setPasteDialogOpen(false)}
                className="rounded-lg border border-slate-300 bg-white px-3 py-1.5 text-xs text-slate-700 hover:bg-slate-50"
              >
                Hủy
              </button>
              <button
                type="button"
                onClick={handleApplyPastedContent}
                className="rounded-lg bg-[var(--color-primary)] px-4 py-1.5 text-xs font-medium text-white hover:bg-[var(--color-primary-hover)]"
              >
                Nạp vào bảng
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
